import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  getUserPreferences,
  updateUserPreferences,
  getStoredTheme,
  storeTheme,
} from '@/services/userPreferencesService';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());
  // Mutex flag — never read during render, so a ref avoids needless re-renders.
  const loadingRef = useRef(false);

  // Load user preferences from Firestore when user logs in
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (currentUser && !loadingRef.current) {
        loadingRef.current = true;
        try {
          const preferences = await getUserPreferences(currentUser.uid);
          if (preferences && preferences.theme !== theme) {
            setThemeState(preferences.theme);
          }
        } catch (error) {
          console.error('Error loading user theme preference:', error);
        } finally {
          loadingRef.current = false;
        }
      }
    };

    if (currentUser) {
      loadUserPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');
    root.classList.add(theme);

    storeTheme(theme);

    if (currentUser && !loadingRef.current) {
      updateUserPreferences(currentUser.uid, { theme }).catch((error) => {
        console.error('Error saving theme preference to Firestore:', error);
      });
    }
  }, [theme, currentUser]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
